import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import CrudTable from "./components/CrudTable";
import EntityFormModal from "./components/EntityFormModal";
import EntityViewModal from "./components/EntityViewModal";
import DeleteConfirmModal from "../users-crud-management/components/DeleteConfirmModal";
import { useDispatch, useSelector } from "react-redux";
import { getUsersService } from "redux/services/users_service";
import { getWorkshopsService } from "redux/services/workshops_service";
import { getAllProductsService } from "redux/services/products_sevice";
import { getVehiclesService } from "redux/services/general_service";
import { entityConfigs } from "./components/entityConfigs";

const MultiEntityCrudHub = () => {
  const dispatch = useDispatch();

  const [language, setLanguage] = useState("en");
  const [activeEntity, setActiveEntity] = useState("users");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formMode, setFormMode] = useState("add");
  const [currentPage, setCurrentPage] = useState(1);

  // ====== Selectors ======
  const { usersList, metaUsers } = useSelector((state) => state.users);
  const { workshopsList, metaWorkshops } = useSelector((state) => state.workshops);
  const { productList, metaProduct } = useSelector((state) => state.products);
  const { vehiclesList, metVehicles } = useSelector((state) => state.general);

  // ====== Current data + meta ======
  const currentData = {
    users: usersList,
    workshops: workshopsList,
    emergency: workshopsList,
    products: productList,
    carTypes: vehiclesList,
  }[activeEntity] || [];
console.log(vehiclesList)
  const currentMeta = {
    users: metaUsers,
    workshops: metaWorkshops,
    emergency: metaWorkshops,
    products: metaProduct,
    carTypes: metVehicles,
  }[activeEntity] || {};

  // ====== Current config ======
  const currentConfig = entityConfigs[activeEntity];

  // ====== Fetch data when entity/page changes ======
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language") || "en";
    setLanguage(savedLanguage);

    if (activeEntity === "users") dispatch(getUsersService({ page: currentPage }));
    if (activeEntity === "workshops") dispatch(getWorkshopsService({ page: currentPage }));
    if (activeEntity === "emergency") dispatch(getWorkshopsService({ emergency: 1, page: currentPage }));
    if (activeEntity === "products") dispatch(getAllProductsService({ page: currentPage }));
    if (activeEntity === "carTypes") dispatch(getVehiclesService({ page: currentPage }));
  }, [activeEntity, currentPage, dispatch]);

  // ====== Labels ======
  const labels = {
    title: {
      en: `Manage ${currentConfig?.labels?.plural?.en || activeEntity}`,
      ar: `إدارة ${currentConfig?.labels?.plural?.ar || activeEntity}`,
    },
    addNew: {
      en: `Add New ${currentConfig?.labels?.singular?.en || activeEntity}`,
      ar: `إضافة ${currentConfig?.labels?.singular?.ar || activeEntity}`,
    },
    search: {
      en: `Search ${currentConfig?.labels?.plural?.en?.toLowerCase() || activeEntity}...`,
      ar: `البحث عن ${currentConfig?.labels?.plural?.ar || activeEntity}...`,
    },
    noResults: {
      en: `No ${currentConfig?.labels?.plural?.en?.toLowerCase()} found`,
      ar: `لم يتم العثور على ${currentConfig?.labels?.plural?.ar}`,
    },
  };

  // ====== Handlers ======
  const handleAddItem = () => { setFormMode("add"); setSelectedItem(null); setIsFormModalOpen(true); };
  const handleEditItem = (item) => { setFormMode("edit"); setSelectedItem(item); setIsFormModalOpen(true); };
  const handleViewItem = (item) => { setSelectedItem(item); setIsViewModalOpen(true); };
  const handleDeleteItem = (item) => { setSelectedItem(item); setIsDeleteModalOpen(true); };
  const handleConfirmDelete = () => { setIsDeleteModalOpen(false); };

  // ====== Filtered data ======
  const filteredData = currentData.filter((item) => {
    if (!searchQuery) return true;
    return currentConfig?.searchFields?.some((field) => {
      const value = field.split('.').reduce((obj, key) => obj?.[key], item);
      return value?.toString()?.toLowerCase().includes(searchQuery.toLowerCase());
    });
  });

  // ====== Tabs ======
  const tabs = ["users", "workshops", "emergency", "products", "carTypes"];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
          {labels.title[language]}
        </h1>

        {/* Tabs */}
        <div className="mb-6 flex gap-2 overflow-x-auto scrollbar-custom">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveEntity(tab); setCurrentPage(1); setSearchQuery(""); }}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeEntity === tab ? "bg-primary text-primary-foreground" : "bg-card text-text-secondary"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeEntity=="users" }

        {/* Search + Add */}
        <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <Input
              type="text"
              placeholder={labels.search[language]}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleAddItem} iconName="Plus">
              {labels.addNew[language]}
            </Button>
          </div>

          {/* Table or No results */}
          {filteredData?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name={currentConfig?.icon || "Package"} size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-text-secondary">{labels.noResults[language]}</p>
            </div>
          ) : (
            <CrudTable
              data={filteredData}
              entityType={activeEntity}
              config={currentConfig}
              language={language}
              onEdit={handleEditItem}
              onDelete={handleDeleteItem}
              onView={handleViewItem}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              meta={currentMeta}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <EntityFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSave={() => {}}
          item={selectedItem}
          mode={formMode}
          entityType={activeEntity}
          config={currentConfig}
          language={language}
        />
      )}

      {isViewModalOpen && (
        <EntityViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          item={selectedItem}
          entityType={activeEntity}
          config={currentConfig}
          language={language}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          itemName={selectedItem?.name || selectedItem?.type}
          language={language}
        />
      )}
    </div>
  );
};

export default MultiEntityCrudHub;
